/**
 * Widgets of DREW's OE Dashboard (Beta-1 plan P3-20): today's sections, one widget each, fed by
 * OEDashboardContext (no data fetching here). Importing this module registers them;
 * `DriftDashboard.jsx` does that. Every widget is a shortcut to its own page (C0813).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Chip, Grid, List, ListItemButton, ListItemText, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { MenuBook as CaseStudiesIcon, ArrowForward as ArrowForwardIcon, RssFeed as ActivityIcon, Update as WhatChangedIcon } from '@mui/icons-material';
import { summarizeChanges } from '../utils/whatChanged';
import REPSQuadrant from '../components/REPSQuadrant';
import OEBreakdown from '../components/OEBreakdown';
import TrendChart from '../components/TrendChart';
import SurveyActivityFeed from '../components/SurveyActivityFeed';
import CaseStudyCard from '../components/CaseStudyCard';
import GuidancePanel from '../components/GuidancePanel';
import { ActiveSurveysCard } from './ActiveSurveysCard.jsx';
import { OEConditionsPanel } from '../components/OEConditionsPanel';
import { useOEDashboard } from './OEDashboardContext.jsx';
import { useRoleDashboard } from './RoleDashboardContext.jsx';
import { widgetRegistry, DASHBOARD_KEYS } from '../../../platform/widgets/widgetRegistry.js';

const ROOT = '/drew';
const DREW_DASHBOARDS = [DASHBOARD_KEYS.drewOe, DASHBOARD_KEYS.drewItm, DASHBOARD_KEYS.drewFls, DASHBOARD_KEYS.drewDh, DASHBOARD_KEYS.drewExec];

export const QUADRANTS = [
  { key: 'reliability', label: 'Reliability' },
  { key: 'efficiency', label: 'Efficiency' },
  { key: 'productivity', label: 'Productivity' },
  { key: 'safety', label: 'Safety' },
];

/* ------------------------------------------------------------------ REPS quadrant cards */

function RepsQuadrantsWidget({ size }) {
  const { latestScores, enrichedScores, warningsByQuadrant, onQuadrantClick, thresholds, latestSnapshot, scoringMode, liveBasis } = useOEDashboard();
  const conditionsOnly = scoringMode === 'conditions';
  const basis = conditionsOnly ? liveBasis : latestSnapshot?.basis || null;
  const twoByTwo = size === 'L';
  return (
    <Grid container spacing={2} sx={{ height: '100%' }} data-tour="reps-quadrants" alignItems="stretch">
      {QUADRANTS.map((q) => (
        <Grid item xs={6} md={twoByTwo ? 6 : 3} key={q.key} sx={{ display: 'flex' }}>
          <Box sx={{ flex: 1, '& > *': { height: '100%' } }}>
            <REPSQuadrant
              label={q.label}
              score={enrichedScores ? enrichedScores[q.key] : latestScores ? latestScores[q.key] : null}
              baseScore={latestScores ? latestScores[q.key] : null}
              adjustmentDelta={enrichedScores?._totalDeltas?.[q.key] || 0}
              adjustmentsApplied={enrichedScores?._adjustmentsApplied || 0}
              quadrantKey={q.key}
              onClick={() => onQuadrantClick(q.key)}
              warning={warningsByQuadrant[q.key] || null}
              threshold={thresholds?.[q.key] ?? 6}
              perception={latestSnapshot?.perceptionScores?.[q.key] ?? null}
              reality={conditionsOnly ? (latestScores ? latestScores[q.key] : null) : latestSnapshot?.conditionScores?.[q.key] ?? null}
              basis={basis}
              mode={scoringMode}
            />
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

/* ------------------------------------------------------------------ OE Element Breakdown */

function OEBreakdownWidget() {
  const { elementScores, thresholds } = useOEDashboard();
  return (
    <Card sx={{ height: '100%', overflow: 'auto' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, '& .MuiTypography-h6': { mb: 1 } }}>
        <OEBreakdown elementScores={elementScores} thresholds={thresholds} />
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ Trend */

function TrendWidget({ size, props }) {
  const { snapshots, thresholds, organization } = useOEDashboard();
  return <TrendChart snapshots={snapshots} thresholds={thresholds} organization={organization} chartHeight={size === 'XL' ? 320 : 200} period={props?.period || '90d'} />;
}

/* ------------------------------------------------------------------ Active surveys (compact = P3-02) */

function ActiveSurveysWidget({ size, props }) {
  const { activeSurveys, surveys, responseCounts, organization } = useOEDashboard();
  const compact = props?.compact != null ? Boolean(props.compact) : size === 'M' || size === 'S';
  return <ActiveSurveysCard activeSurveys={activeSurveys} surveys={surveys} responseCounts={responseCounts} compact={compact} organization={organization} />;
}

/* ------------------------------------------------------------------ Recent activity */

function RecentActivityWidget({ size, props }) {
  const maxItems = Number(props?.maxItems) || (size === 'M' ? 4 : 10);
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ActivityIcon sx={{ mr: 1, color: 'secondary.main' }} />
          <Typography variant="h6">Recent Activity</Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <SurveyActivityFeed maxItems={maxItems} />
        </Box>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ Case studies */

function CaseStudiesWidget() {
  const navigate = useNavigate();
  const { caseStudies, caseStudiesLoading } = useOEDashboard();
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CaseStudiesIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Case Studies</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* P5-14 (Tim D03 B): Add Documents → Document Upload Portal */}
            <Button size="small" onClick={() => navigate(`${ROOT}/documents`)} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Add Documents
            </Button>
            <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate(`${ROOT}/case-studies`)} sx={{ textTransform: 'none', fontWeight: 600 }}>
              View All
            </Button>
          </Box>
        </Box>
        {caseStudiesLoading ? (
          <Box sx={{ display: 'flex', gap: 2, overflow: 'hidden' }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" width={300} height={120} sx={{ borderRadius: 2, flexShrink: 0 }} />
            ))}
          </Box>
        ) : caseStudies.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No case studies available yet. Check back later for CSB reports, audits, and observations.
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 1,
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: 3 },
            }}
          >
            {caseStudies.map((cs) => (
              <Box key={cs.id} sx={{ minWidth: 280, maxWidth: 340, flexShrink: 0 }}>
                <CaseStudyCard caseStudy={cs} compact />
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ Personalized guidance */

function GuidanceWidget({ size }) {
  const { latestScores, preview } = useOEDashboard();
  if (!latestScores) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Personalized Guidance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {preview ? 'Recommendations ranked by your lowest REPS scores.' : 'Guidance appears once the first survey scores are in.'}
          </Typography>
        </CardContent>
      </Card>
    );
  }
  return (
    <Box sx={{ height: '100%', overflow: 'auto', '& > .MuiCard-root': { mb: 0, height: '100%' } }}>
      <GuidancePanel assessmentType="drift_accumulation" scores={latestScores} maxItems={size === 'XL' ? 5 : 3} />
    </Box>
  );
}

/* ------------------------------------------------------------------ OE Conditions (P3-01, Tim: "listed at the bottom of every dashboard") */

function OEConditionsWidget({ size, props }) {
  const { conditions: pageConditions, allConditions, scope: pageScope, organization } = useOEDashboard();
  const role = useRoleDashboard(); // on an access-level dashboard: the persona's items + what they own (P2-14)
  // P3-18: a per-card scope override ('site:ID' | 'department:ID' | 'team:ID'), else the page filter.
  const override = parseScopeValue(props?.scope);
  const scope = override || pageScope;
  const conditions = override ? filterConditionsByScope(allConditions || pageConditions, override) : pageConditions;
  const roleItems = role && !override ? role.mergedConditions : null;
  const roleLabel = role?.conditionsScopeLabel || null;
  const scopeLabel = roleLabel && !override ? roleLabel :
    !scope || scope.type === 'organization'
      ? 'all sites'
      : scope.type === 'site'
        ? (organization?.sites || []).find((s) => s.id === scope.id)?.name || 'site'
        : scope.type === 'department'
          ? (organization?.departments || []).find((d) => d.id === scope.id)?.name || 'department'
          : (organization?.teams || []).find((t) => t.id === scope.id)?.name || 'NWT';
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pb: 1 }}>
        <OEConditionsPanel items={roleItems || conditions} title={role?.conditionsTitle || 'OE Conditions'} scopeLabel={scopeLabel} defaultFilter={role?.conditionsDefaultFilter} compact={size === 'W' || size === 'L'} maxRows={size === 'W' ? 4 : size === 'L' ? 8 : 50} />
      </CardContent>
    </Card>
  );
}


/* ------------------------------------------------------------------ What changed (P3-05, Tim C0813 plant manager landing) */

const CHANGE_KIND = {
  warning: { label: 'Warning', color: 'error' },
  new: { label: 'New', color: 'primary' },
  changed: { label: 'Changed', color: 'default' },
  resolved: { label: 'Resolved', color: 'success' },
};

function WhatChangedWidget({ size, props }) {
  const navigate = useNavigate();
  const { snapshots, conditions, preview } = useOEDashboard();
  const days = Number(props?.days) || 7;
  const { items, counts } = React.useMemo(() => summarizeChanges({ snapshots, conditions, days }), [snapshots, conditions, days]);
  const max = size === 'M' || size === 'S' ? 4 : size === 'XL' ? 12 : 7;
  const ago = (at) => {
    if (!at) return '';
    const d = Math.floor((Date.now() - at) / 86400000);
    return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d}d ago`;
  };
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <WhatChangedIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">What changed</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>last {days} days</Typography>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap' }} useFlexGap>
          {Object.entries(CHANGE_KIND).map(([kind, k]) => (
            <Chip key={kind} size="small" label={`${counts[kind]} ${k.label.toLowerCase()}`} color={counts[kind] ? k.color : 'default'} variant={counts[kind] ? 'filled' : 'outlined'} />
          ))}
        </Stack>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {preview ? 'New warnings, new / changed / resolved OE Conditions in your scope.' : 'Nothing changed in this scope — no new warnings, no condition activity.'}
          </Typography>
        ) : (
          <List dense disablePadding sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {items.slice(0, max).map((it, idx) => (
              <ListItemButton
                key={`${it.kind}-${it.trlId || it.quadrant || idx}`}
                dense
                sx={{ px: 1, borderRadius: 1 }}
                onClick={() => navigate(it.trlId ? `${ROOT}/rollup/trl-entry?condition=${it.trlId}` : ROOT)}
              >
                <Chip size="small" label={CHANGE_KIND[it.kind].label} color={CHANGE_KIND[it.kind].color} sx={{ mr: 1, minWidth: 72 }} />
                <ListItemText primary={it.title} secondary={`${it.detail} · ${ago(it.at)}`} primaryTypographyProps={{ noWrap: true, variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
              </ListItemButton>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}

/** 'site:abc' → { type: 'site', id: 'abc' }; '' / unknown → null. */
export function parseScopeValue(value) {
  if (!value || typeof value !== 'string') return null;
  const idx = value.indexOf(':');
  if (idx < 1) return null;
  const type = value.slice(0, idx);
  const id = value.slice(idx + 1);
  return ['site', 'department', 'team'].includes(type) && id ? { type, id } : null;
}

export function filterConditionsByScope(list, scope) {
  if (!scope || scope.type === 'organization') return list || [];
  if (scope.type === 'site') return (list || []).filter((c) => c.siteId === scope.id);
  if (scope.type === 'department') return (list || []).filter((c) => c.deptId === scope.id || c.ownerDeptId === scope.id);
  if (scope.type === 'team') return (list || []).filter((c) => c.nwtId === scope.id || c.ownerNwtId === scope.id);
  return list || [];
}

/* ------------------------------------------------------------------ registration */

const DEFS = [
  {
    id: 'what-changed',
    title: 'What changed',
    description: 'New warnings and new / changed / resolved OE Conditions in your scope over the last 7 days.',
    category: 'alerts',
    component: WhatChangedWidget,
    sizes: ['M', 'L', 'XL'],
    defaultSize: 'L',
    dashboards: DREW_DASHBOARDS,
    frameless: true,
    settingsSchema: [{ key: 'days', label: 'Window', type: 'select', options: [{ value: 7, label: 'Last 7 days' }, { value: 14, label: 'Last 14 days' }, { value: 30, label: 'Last 30 days' }], default: 7 }],
  },
  {
    id: 'reps-quadrants',
    title: 'REPS Quadrants',
    description: 'Reliability, Efficiency, Productivity and Safety scores with warnings; click a card to drill down.',
    category: 'scores',
    component: RepsQuadrantsWidget,
    sizes: ['W', 'L', 'XL'],
    defaultSize: 'W',
    href: `${ROOT}/ia/trl-system`,
    dashboards: DREW_DASHBOARDS,
    frameless: true,
  },
  {
    id: 'oe-breakdown',
    title: 'OE Element Breakdown',
    description: 'Every OE element of the latest snapshot, color-coded by threshold band.',
    category: 'scores',
    component: OEBreakdownWidget,
    sizes: ['W', 'L', 'XL'],
    defaultSize: 'W',
    dashboards: DREW_DASHBOARDS,
    frameless: true,
    scroll: true,
  },
  {
    id: 'oe-trend',
    title: 'Trend',
    description: 'OE score over time against the threshold bands (30 d / 90 d / 12 m / custom).',
    category: 'scores',
    component: TrendWidget,
    sizes: ['L', 'XL'],
    defaultSize: 'XL',
    settingsSchema: [{ key: 'period', label: 'Period', type: 'period', default: '90d', help: 'Two Trend cards with different periods can sit side by side.' }],
    dashboards: DREW_DASHBOARDS,
    frameless: true,
    scroll: true,
  },
  {
    id: 'active-surveys',
    title: 'Active Surveys',
    description: 'Surveys you can take or that are collecting responses in your scope; compact at Medium.',
    category: 'surveys',
    component: ActiveSurveysWidget,
    sizes: ['M', 'L', 'XL'],
    defaultSize: 'M',
    // Compact by default — Tim, C0820: "way too much real estate for the surveys … there will likely
    // only ever be one for anybody other than the administrator" (UI review 2026-09-21 §5).
    settingsSchema: [{ key: 'compact', label: 'Compact list (max 3 + View all)', type: 'toggle', default: true }],
    href: `${ROOT}/surveys`,
    dashboards: DREW_DASHBOARDS,
    frameless: true,
  },
  {
    id: 'recent-activity',
    title: 'Recent Activity',
    description: 'The latest survey and OE Condition events in your organization.',
    category: 'activity',
    component: RecentActivityWidget,
    sizes: ['M', 'L', 'XL'],
    defaultSize: 'L',
    settingsSchema: [{ key: 'maxItems', label: 'Events shown', type: 'select', options: [{ value: 5, label: '5' }, { value: 10, label: '10' }, { value: 20, label: '20' }], default: 10 }],
    dashboards: DREW_DASHBOARDS,
    frameless: true,
  },
  {
    id: 'case-studies',
    title: 'Case Studies',
    description: 'Industry case studies from the REPSit Learning Library.',
    category: 'library',
    component: CaseStudiesWidget,
    sizes: ['W', 'L', 'XL'],
    defaultSize: 'W',
    href: `${ROOT}/case-studies`,
    dashboards: DREW_DASHBOARDS,
    frameless: true,
  },
  {
    id: 'oe-conditions',
    title: 'OE Conditions',
    description: 'Every OE Condition in your scope — ID, type, severity, status, owner — with what needs your action.',
    category: 'oe_conditions',
    component: OEConditionsWidget,
    sizes: ['W', 'L', 'XL'],
    defaultSize: 'XL',
    settingsSchema: [{ key: 'scope', label: 'Scope', type: 'scope', help: 'Pin this card to one site, department or NWT within your access.' }],
    href: `${ROOT}/rollup/trl-entry`,
    dashboards: DREW_DASHBOARDS,
    frameless: true,
  },
  {
    id: 'guidance',
    title: 'Personalized Guidance',
    description: 'Recommendations ranked by your lowest REPS scores.',
    category: 'guidance',
    component: GuidanceWidget,
    sizes: ['L', 'XL'],
    defaultSize: 'XL',
    dashboards: DREW_DASHBOARDS,
    frameless: true,
    scroll: true,
  },
];

for (const def of DEFS) widgetRegistry.upsert(def);

export const OE_DASHBOARD_WIDGET_IDS = DEFS.map((d) => d.id);
